import { useCallback, useEffect, useRef, useState } from 'react';
import { Sparkles, X } from 'lucide-react';
import { useRecoilState, useRecoilValue } from 'recoil';

import { useAudio, useConfig, sessionState } from '@chainlit/react-client';
import { activeAudioButtonState } from '@/state/chat';
import { useUISettings } from '@/hooks/useUISettings';

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';

// Signature Aviation Brand Colors
const BRAND_COLORS = {
  runway: '#161D25',      // Dark navy/black
  midnightSky: '#152644', // Deep blue
  landing: '#ECF2FF',     // Light blue/white
  altitude: '#FFFFFF',    // White
  wingspan: '#354646',    // Dark teal
  flightPlan: '#63647D',  // Gray
  jet: '#10DC9E',         // Bright teal/green (accent)
};

/**
 * AI Avatar button for the header - provides elegant access to Realtime Assistant.
 * Shows as a glowing avatar that expands into full-screen mode on click.
 * Supports Alt+A keyboard shortcut.
 */
export default function AIAvatarButton({ onOverlayToggle }: { onOverlayToggle?: () => void }) {
  const { config } = useConfig();
  const { settings } = useUISettings();
  const { startConversation, endConversation, audioConnection } = useAudio();
  const [activeButton, setActiveButton] = useRecoilState(activeAudioButtonState);
  const session = useRecoilValue(sessionState);
  // Check if assistant mode is enabled
  const isEnabled = !!config?.features.audio.enabled && !!settings?.assistantMicEnabled;
  const isActive = audioConnection === 'on' && activeButton === 'assistant';

  const activateAssistant = useCallback(() => {
    if (session?.socket) {
      session.socket.emit('audio_source_select', { sourceType: 'assistant' });
    }
    setActiveButton('assistant');
    startConversation();
  }, [session, setActiveButton, startConversation]);

  const deactivateAssistant = useCallback(() => {
    endConversation();
    setActiveButton(null);
  }, [endConversation, setActiveButton]);

  const handleClick = useCallback(() => {
    if (isActive) {
      // If already active, clicking again reopens the overlay
      onOverlayToggle?.();
    } else {
      activateAssistant();
    }
  }, [isActive, activateAssistant, onOverlayToggle]);

  // Alt+A key shortcut handling
  useEffect(() => {
    if (!isEnabled) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Alt + A to toggle assistant
      if (e.altKey && (e.key === 'a' || e.key === 'A') && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
        // Prevent default behavior
        e.preventDefault();
        
        // Toggle assistant on/off
        if (isActive) {
          deactivateAssistant();
        } else {
          activateAssistant();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isEnabled, isActive, activateAssistant, deactivateAssistant]);

  if (!isEnabled) return null;

  return (
    <div className="flex items-center gap-2">
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={handleClick}
              className={`
                relative flex items-center justify-center
                w-10 h-10 rounded-full
                transition-all duration-300 ease-out
                border-2
                ${isActive 
                  ? 'border-[#10DC9E] bg-[#152644] scale-110 shadow-lg shadow-[#10DC9E]/30' 
                  : 'border-[#152644] bg-[#152644]/10 hover:bg-[#152644]/20 hover:scale-105'
                }
              `}
            >
              {/* Pulsing ring animation when active */}
              {isActive && (
                <>
                  <div 
                    className="absolute inset-0 rounded-full animate-ping opacity-30"
                    style={{ backgroundColor: BRAND_COLORS.jet }}
                  />
                  <div 
                    className="absolute inset-0 rounded-full animate-pulse opacity-50"
                    style={{ 
                      background: `linear-gradient(135deg, ${BRAND_COLORS.midnightSky}, ${BRAND_COLORS.jet})` 
                    }}
                  />
                </>
              )}
              
              {/* Icon */}
              <Sparkles 
                className={`
                  relative z-10 w-5 h-5
                  transition-all duration-300
                  ${isActive 
                    ? 'text-[#10DC9E] drop-shadow-[0_0_8px_rgba(16,220,158,0.8)]' 
                    : 'text-[#152644]'
                  }
                `}
              />

              {/* Ambient glow when active */}
              {isActive && (
                <div 
                  className="absolute inset-0 rounded-full blur-xl opacity-60"
                  style={{
                    background: `radial-gradient(circle, ${BRAND_COLORS.jet}60 0%, ${BRAND_COLORS.midnightSky}40 50%, transparent 70%)`,
                  }}
                />
              )}
            </button>
          </TooltipTrigger>
          <TooltipContent 
            className="bg-[#152644] text-white border-[#354646]"
          >
            <p className="text-xs flex items-center gap-2">
              {isActive 
                ? 'Reopen Overlay' 
                : <>Start AI Assistant <kbd className="px-1.5 py-0.5 bg-[#354646] rounded text-[10px]">Alt+A</kbd></>
              }
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {/* Stop button - only visible when active */}
      {isActive && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={deactivateAssistant}
                className={`
                  relative flex items-center justify-center
                  w-8 h-8 rounded-full
                  transition-all duration-300 ease-out
                  border-2 border-red-500/50 bg-red-500/10
                  hover:bg-red-500/20 hover:border-red-500 hover:scale-110
                  animate-in fade-in slide-in-from-left-2 duration-300
                `}
              >
                <X className="w-4 h-4 text-red-500" />
              </button>
            </TooltipTrigger>
            <TooltipContent 
              className="bg-[#152644] text-white border-[#354646]"
            >
              <p className="text-xs flex items-center gap-2">
                Stop AI Assistant <kbd className="px-1.5 py-0.5 bg-[#354646] rounded text-[10px]">Alt+A</kbd>
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
}
